/**
 * YouSheet — the "You" destination. A bottom sheet (mobile) / centered modal
 * (desktop) that holds everything demoted out of the old top nav: Settings,
 * About, the Suggestion box, Terms/Privacy, and Sign out.
 */

import { useEffect } from 'react';
import { X, Settings, Info, Lightbulb, LogOut, Compass, PlayCircle } from 'lucide-react';

export default function YouSheet({ open, email, onClose, onSettings, onAbout, onSuggestion, onTerms, onPrivacy, onSignOut, onTour, onVideo }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const items = [
    { icon: Settings, label: 'Settings', onClick: onSettings },
    { icon: Info, label: 'About The Navigator', onClick: onAbout },
    { icon: Lightbulb, label: 'Suggestion Box', onClick: onSuggestion },
    { icon: Compass, label: 'Show me around', onClick: () => { onClose?.(); onTour?.(); } },
    { icon: PlayCircle, label: 'How to use the Navigator (2½ min)', onClick: () => { onClose?.(); onVideo?.(); } },
  ];

  return (
    <div className="fixed inset-0 z-30 flex items-end sm:items-center justify-center" role="dialog" aria-modal="true" aria-label="You">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full sm:max-w-sm bg-slate-900 border border-slate-700 rounded-t-2xl sm:rounded-2xl shadow-soft p-4 pb-safe-2 animate-slide-up">
        <div className="flex items-center justify-between mb-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-100">You</p>
            {email && <p className="text-xs text-slate-500 truncate">{email}</p>}
          </div>
          <button onClick={onClose} aria-label="Close" className="text-slate-500 hover:text-slate-300 p-1 -m-1">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-1">
          {items.map(({ icon: Icon, label, onClick }) => (
            <button
              key={label}
              onClick={onClick}
              className="w-full flex items-center gap-3 px-3 rounded-lg min-h-tap text-sm text-slate-200 hover:bg-slate-800 transition-colors text-left"
            >
              <Icon size={16} className="text-slate-400 flex-shrink-0" /> {label}
            </button>
          ))}
        </div>

        <div className="border-t border-slate-800 mt-2 pt-2">
          <button
            onClick={onSignOut}
            className="w-full flex items-center gap-3 px-3 rounded-lg min-h-tap text-sm text-red-300 hover:bg-red-950/30 transition-colors text-left"
          >
            <LogOut size={16} className="flex-shrink-0" /> Sign out
          </button>
        </div>

        <div className="flex items-center justify-center gap-3 mt-3 text-[11px] text-slate-600">
          <button onClick={onTerms} className="hover:text-slate-400 transition-colors">Terms</button>
          <span>·</span>
          <button onClick={onPrivacy} className="hover:text-slate-400 transition-colors">Privacy</button>
        </div>
      </div>
    </div>
  );
}
